import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Clock, Receipt, ChevronRight } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { useOrgSnapshot } from "../../hooks/useOrgSnapshot";
import { Proposals, type Proposal } from "../../lib/apiTyped";

const LIVE_SUB_STATUSES = new Set([
  "active",
  "trialing",
  "past_due",
  "incomplete",
]);

// Persistent owner alerts shown above every page in AppLayout. Two
// concerns today: missing fiscal data (blocks Stripe checkout) and
// outstanding one-time proposals waiting for payment. Each banner is
// click-through to the relevant page.
export function OwnerAlerts() {
  const { effectiveRole } = useAuth();
  const { org, loading } = useOrgSnapshot();
  const navigate = useNavigate();
  const [pending, setPending] = useState<Proposal[]>([]);

  const isOwner = effectiveRole === "enterprise_owner";

  useEffect(() => {
    if (!isOwner) return;
    let cancelled = false;
    Proposals.list()
      .then((res) => {
        if (cancelled) return;
        setPending(res.proposals.filter((p) => p.status === "sent"));
      })
      .catch(() => {
        // Silent — banner is optional UX; toast layer handles real errors.
      });
    return () => {
      cancelled = true;
    };
  }, [isOwner]);

  if (!isOwner) return null;
  if (loading) return null;

  const hasFiscal = Boolean((org as { fiscal?: unknown } | null)?.fiscal);
  const orgFields = (org as {
    activeTier?: string | null;
    subscriptionStatus?: string | null;
  } | null) ?? null;
  const hasAssignedTier = Boolean(orgFields?.activeTier);
  const subscriptionLive = Boolean(
    orgFields?.subscriptionStatus &&
      LIVE_SUB_STATUSES.has(orgFields.subscriptionStatus),
  );
  const subscriptionPendingActivation = hasAssignedTier && !subscriptionLive;

  if (hasFiscal && !subscriptionPendingActivation && pending.length === 0)
    return null;

  return (
    <div className="space-y-2 mb-4">
      {!hasFiscal && (
        <Banner
          tone="warning"
          icon={<AlertCircle className="w-4 h-4" />}
          message="Complete your billing details to activate subscriptions and receive invoices."
          cta="Complete"
          onClick={() => navigate("/billing")}
        />
      )}
      {subscriptionPendingActivation && (
        <Banner
          tone="warning"
          icon={<Clock className="w-4 h-4" />}
          message="Your plan is assigned but pending activation. Seats won't be activated until payment is processed."
          cta="Activate"
          onClick={() => navigate("/api/subscription")}
        />
      )}
      {pending.length > 0 && (
        <Banner
          tone="info"
          icon={<Receipt className="w-4 h-4" />}
          message={`You have ${pending.length} pending payment${pending.length === 1 ? "" : "s"}.`}
          cta="View"
          onClick={() => navigate("/billing")}
        />
      )}
    </div>
  );
}

function Banner({
  tone,
  icon,
  message,
  cta,
  onClick,
}: {
  tone: "warning" | "info";
  icon: React.ReactNode;
  message: string;
  cta: string;
  onClick: () => void;
}) {
  const bg =
    tone === "warning"
      ? "rgba(255,193,7,0.10)"
      : "rgba(59,130,246,0.10)";
  const border =
    tone === "warning"
      ? "rgba(255,193,7,0.35)"
      : "rgba(59,130,246,0.35)";
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm transition-opacity hover:opacity-90 text-left"
      style={{ backgroundColor: bg, border: `1px solid ${border}` }}
    >
      <span className="text-ink-2 shrink-0">{icon}</span>
      <span className="flex-1 text-ink-2">{message}</span>
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-ink">
        {cta}
        <ChevronRight className="w-3.5 h-3.5" />
      </span>
    </button>
  );
}
